import type { User } from '../../shared/types';

/**
 * Gradient-initials avatar derived from the user's hue + name.
 * Aesthetic, deterministic, zero external dependency. If avatarUrl is set
 * (e.g. SSO profile picture in a future provider), we render that instead.
 */
export function Avatar({
  user,
  size = 32,
  ring = false,
}: {
  user: Pick<User, 'id' | 'name' | 'avatarHue' | 'avatarUrl'>;
  size?: number;
  ring?: boolean;
}) {
  const initials = user.name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');

  const h = user.avatarHue;
  const bg = `linear-gradient(135deg,
    hsl(${h} 60% 36%) 0%,
    hsl(${(h + 30) % 360} 65% 22%) 100%)`;

  return (
    <span
      title={user.name}
      style={{
        width: size,
        height: size,
        background: bg,
        boxShadow: ring ? '0 0 0 2px var(--color-ink), 0 0 0 3px hsl(' + h + ' 65% 55%)' : undefined,
      }}
      className="inline-flex select-none items-center justify-center rounded-full font-medium text-cream"
    >
      {user.avatarUrl ? (
        <img
          src={user.avatarUrl}
          alt={user.name}
          className="h-full w-full rounded-full object-cover"
          referrerPolicy="no-referrer"
        />
      ) : (
        <span style={{ fontSize: size * 0.4 }}>{initials || '?'}</span>
      )}
    </span>
  );
}

export function AvatarStack({
  users,
  size = 28,
  max = 4,
}: {
  users: Array<Pick<User, 'id' | 'name' | 'avatarHue' | 'avatarUrl'>>;
  size?: number;
  max?: number;
}) {
  const shown = users.slice(0, max);
  const overflow = users.length - shown.length;
  return (
    <div className="flex items-center">
      {shown.map((u, i) => (
        <span
          key={u.id}
          style={{ marginLeft: i === 0 ? 0 : -size * 0.35 }}
          className="inline-block"
        >
          <Avatar user={u} size={size} ring />
        </span>
      ))}
      {overflow > 0 && (
        <span
          style={{ width: size, height: size, marginLeft: -size * 0.35, fontSize: size * 0.36 }}
          className="inline-flex items-center justify-center rounded-full border border-ink-rim bg-ink-3 text-cream-2"
        >
          +{overflow}
        </span>
      )}
    </div>
  );
}
